import React, { useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Map as MapIcon, Sparkles, Shield, Crown, BookOpen, ScrollText, Landmark, Users, Trophy, ChevronRight, X, Star, Bus, Ticket, ArrowRight, Flame, History, Gem, GraduationCap, HeartHandshake } from 'lucide-react';
import { Link } from 'react-router-dom';
// --- MOCK DATA ---
const ZONES = [{
  id: 'lahore-court',
  name: 'Lahore Court',
  description: 'Restore the portraits, seals, court scenes, and authority of the Lahore Darbar.',
  accepted: 'Portraits, seals, court scenes',
  progress: 42,
  cardsContributed: 128,
  requiredCards: 300,
  icon: Landmark,
  color: 'from-[#D4AF37]/20 to-transparent',
  borderColor: 'border-[#D4AF37]/30'
}, {
  id: 'sacred-punjab',
  name: 'Sacred Punjab',
  description: 'Restore the sacred objects, sites, and symbols of reverence before rule.',
  accepted: 'Harmandir Sahib, Nishan Sahib, Hukamnama',
  progress: 68,
  cardsContributed: 85,
  requiredCards: 125,
  icon: Flame,
  color: 'from-amber-500/20 to-transparent',
  borderColor: 'border-amber-500/30'
}, {
  id: 'frontier-defenders',
  name: 'Frontier Defenders',
  description: 'Restore the forts, standards, shields, and defenders of the empire’s edge.',
  accepted: 'Hari Singh Nalwa, Jamrud Fort, shields',
  progress: 24,
  cardsContributed: 45,
  requiredCards: 180,
  icon: Shield,
  color: 'from-slate-400/20 to-transparent',
  borderColor: 'border-slate-400/30'
}, {
  id: 'treasury-vault',
  name: 'Treasury Vault',
  description: 'Restore the jewels, regalia, coins, and objects of sovereign prestige.',
  accepted: 'Koh-i-Noor, Gold Mohur, Sarpech',
  progress: 89,
  cardsContributed: 210,
  requiredCards: 235,
  icon: Gem,
  color: 'from-emerald-500/20 to-transparent',
  borderColor: 'border-emerald-500/30'
}, {
  id: 'royal-women',
  name: 'Royal Women & Chamber Culture',
  description: 'Restore the inner court of lineage, counsel, music, memory, and refinement.',
  accepted: 'Maharani Jind Kaur, Moran Sarkar',
  progress: 15,
  cardsContributed: 22,
  requiredCards: 150,
  icon: Crown,
  color: 'from-rose-500/20 to-transparent',
  borderColor: 'border-rose-500/30'
}, {
  id: 'fracture-memory',
  name: 'Fracture & Memory',
  description: 'Restore the letters, broken seals, lost treasures, and emotional memory.',
  accepted: 'Broken Seal, Letter from Exile',
  progress: 5,
  cardsContributed: 8,
  requiredCards: 100,
  icon: History,
  color: 'from-indigo-500/20 to-transparent',
  borderColor: 'border-indigo-500/30'
}];
const USER_CARDS = [{
  id: 1,
  name: 'Maharaja Ranjit Singh',
  rarity: "Founders' Edition",
  value: 50,
  img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png'
}, {
  id: 2,
  name: 'Hari Singh Nalwa',
  rarity: 'Epic',
  value: 25,
  img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png'
}, {
  id: 3,
  name: 'Gold Mohur',
  rarity: 'Rare',
  value: 15,
  img: 'https://cdn.magicpatterns.com/uploads/b9oEVYjaVfcwC8HyTuv84g/image.png'
}];
export function RestorePunjabPage() {
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [isContributeModalOpen, setIsContributeModalOpen] = useState(false);
  const [animatingCard, setAnimatingCard] = useState<number | null>(null);
  const handleContribute = (cardId: number) => {
    setAnimatingCard(cardId);
    setTimeout(() => {
      setAnimatingCard(null);
      setIsContributeModalOpen(false);
      // Show success toast here in real app
    }, 1500);
  };
  return <div className="min-h-screen bg-[#0B0C10] text-[#FDFBF7] font-sans selection:bg-[#D4AF37]/30" data-id="element-6359">
      {/* Navigation Bar (Simplified for standalone page) */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0C10]/80 backdrop-blur-md border-b border-white/5" data-id="element-6360">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between" data-id="element-6361">
          <Link to="/" className="font-serif text-xl text-[#D4AF37] tracking-wider" data-id="element-6362">
            THE KINGDOMS OF PUNJAB
          </Link>
          <div className="flex items-center gap-6" data-id="element-6363">
            <Link to="/treasure" className="text-sm text-white/60 hover:text-white transition-colors" data-id="element-6364">
              Lahore Treasury
            </Link>
            <button onClick={() => setIsContributeModalOpen(true)} className="bg-[#D4AF37] text-black px-5 py-2 rounded-sm text-sm font-medium hover:bg-[#FDFBF7] transition-colors" data-id="element-6365">
              Contribute a Card
            </button>
          </div>
        </div>
      </nav>

      {/* SECTION 1: Restoration Hero */}
      <section className="relative pt-32 pb-20 min-h-[90vh] flex flex-col items-center justify-center overflow-hidden" data-id="element-6366">
        {/* Background Map Graphic */}
        <div className="absolute inset-0 z-0 opacity-20 pointer-events-none flex items-center justify-center" data-id="element-6367">
          <div className="w-[800px] h-[800px] rounded-full border border-[#D4AF37]/20 absolute animate-[spin_120s_linear_infinite]" data-id="element-6368" />
          <div className="w-[600px] h-[600px] rounded-full border border-[#D4AF37]/10 absolute animate-[spin_90s_linear_infinite_reverse]" data-id="element-6369" />
          <MapIcon className="w-[400px] h-[400px] text-[#D4AF37] opacity-10" data-id="element-6370" />
        </div>

        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0B0C10]/80 to-[#0B0C10] z-0" data-id="element-6371" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center" data-id="element-6372">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} data-id="element-6373">
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-6 flex items-center justify-center gap-3" data-id="element-6374">
              <Sparkles className="w-4 h-4" data-id="element-6375" />
              The Punjab Memory Map
              <Sparkles className="w-4 h-4" data-id="element-6376" />
            </p>
            <h1 className="font-serif text-5xl sm:text-7xl text-[#FDFBF7] mb-6 leading-tight" data-id="element-6377">
              Restore the Memory <br className="hidden sm:block" data-id="element-6378" />
              <span className="text-[#D4AF37]" data-id="element-6379">of Punjab</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/60 font-light max-w-2xl mx-auto mb-12 leading-relaxed" data-id="element-6380">
              Collect Lahore Treasury cards and use them to illuminate the lost
              places, people, treasures, and stories of the Sikh Empire.
            </p>

            {/* Progress Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12" data-id="element-6381">
              {[{
              label: 'Overall Restoration',
              value: '34%'
            }, {
              label: 'Cards Contributed',
              value: '18'
            }, {
              label: 'Zones Restored',
              value: '2 / 6'
            }, {
              label: 'Next Unlock',
              value: 'Treasury Night'
            }].map((stat, i) => <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-sm" data-id="element-6382">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1" data-id="element-6383">
                    {stat.label}
                  </p>
                  <p className="text-[#D4AF37] font-serif text-2xl" data-id="element-6384">
                    {stat.value}
                  </p>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-6385">
              <button onClick={() => setIsContributeModalOpen(true)} className="w-full sm:w-auto bg-[#D4AF37] text-black px-8 py-4 rounded-sm text-sm font-medium tracking-wide hover:bg-[#FDFBF7] transition-all shadow-[0_0_30px_rgba(212,175,55,0.3)]" data-id="element-6386">
                Start Restoring
              </button>
              <Link to="/treasure" className="w-full sm:w-auto bg-white/5 border border-white/10 text-white px-8 py-4 rounded-sm text-sm font-medium tracking-wide hover:bg-white/10 transition-all" data-id="element-6387">
                Open a Court Packet
              </Link>
              <a href="#schools" className="w-full sm:w-auto text-white/60 hover:text-[#D4AF37] px-6 py-4 text-sm font-medium tracking-wide transition-colors" data-id="element-6388">
                For Schools →
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: How Restoration Works */}
      <section className="py-24 bg-[#0B0C10] relative border-t border-white/5" data-id="element-6389">
        <div className="max-w-7xl mx-auto px-6" data-id="element-6390">
          <div className="text-center mb-16" data-id="element-6391">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-6392">
              How Restoration Works
            </h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" data-id="element-6393" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-6394">
            {[{
            icon: ScrollText,
            title: 'Open Packets',
            desc: 'Reveal portraits, seals, treasures, and memory fragments.'
          }, {
            icon: BookOpen,
            title: 'Learn the Story',
            desc: 'Each card includes lore, historical notes, and audio.'
          }, {
            icon: MapIcon,
            title: 'Choose a Zone',
            desc: 'Contribute cards to specific historical regions.'
          }, {
            icon: Sparkles,
            title: 'Illuminate Map',
            desc: 'Restore visual detail, story fragments, and progress.'
          }, {
            icon: Ticket,
            title: 'Unlock Live',
            desc: 'Reach milestones to unlock Live Night rewards.'
          }].map((step, i) => <div key={i} className="relative" data-id="element-6395">
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 h-full hover:border-[#D4AF37]/30 transition-colors group" data-id="element-6396">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-6397">
                    <step.icon className="w-5 h-5 text-[#D4AF37]" data-id="element-6398" />
                  </div>
                  <h3 className="font-serif text-lg text-white mb-2" data-id="element-6399">
                    {step.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed" data-id="element-6400">
                    {step.desc}
                  </p>
                </div>
                {i < 4 && <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-px bg-white/10" data-id="element-6401" />}
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 3: Restoration Zones */}
      <section className="py-24 bg-[#111218] relative border-t border-white/5" data-id="element-6402">
        <div className="max-w-7xl mx-auto px-6" data-id="element-6403">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6" data-id="element-6404">
            <div data-id="element-6405">
              <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-6406">
                Restoration Zones
              </h2>
              <p className="text-white/60 max-w-2xl" data-id="element-6407">
                Contribute your collectibles to illuminate specific regions of
                the Punjab Memory Map. Each zone requires different types of
                historical artifacts.
              </p>
            </div>
            <button onClick={() => setIsContributeModalOpen(true)} className="bg-white/5 border border-[#D4AF37]/30 text-[#D4AF37] px-6 py-3 rounded-sm text-sm hover:bg-[#D4AF37]/10 transition-colors shrink-0" data-id="element-6408">
              Contribute to a Zone
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6409">
            {ZONES.map(zone => <div key={zone.id} className={`bg-black/40 border ${zone.borderColor} rounded-2xl overflow-hidden group hover:shadow-[0_0_30px_rgba(212,175,55,0.1)] transition-all`} data-id="element-6410">
                <div className={`h-32 bg-gradient-to-br ${zone.color} relative p-6 flex items-end`} data-id="element-6411">
                  <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 text-xs text-white/80" data-id="element-6412">
                    {zone.progress}% Restored
                  </div>
                  <zone.icon className="absolute top-4 left-4 w-6 h-6 text-white/30" data-id="element-6413" />
                  <h3 className="font-serif text-2xl text-white relative z-10" data-id="element-6414">
                    {zone.name}
                  </h3>
                </div>

                <div className="p-6" data-id="element-6415">
                  <p className="text-white/60 text-sm mb-6 h-10" data-id="element-6416">
                    {zone.description}
                  </p>

                  <div className="mb-6" data-id="element-6417">
                    <div className="flex justify-between text-xs mb-2" data-id="element-6418">
                      <span className="text-white/40" data-id="element-6419">Progress</span>
                      <span className="text-[#D4AF37]" data-id="element-6420">
                        {zone.cardsContributed} / {zone.requiredCards} Cards
                      </span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden" data-id="element-6421">
                      <div className="h-full bg-[#D4AF37] rounded-full transition-all duration-1000" style={{
                    width: `${zone.progress}%`
                  }} data-id="element-6422" />
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-lg p-3 mb-6 border border-white/5" data-id="element-6423">
                    <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1" data-id="element-6424">
                      Accepted Cards
                    </p>
                    <p className="text-sm text-white/80" data-id="element-6425">{zone.accepted}</p>
                  </div>

                  <button onClick={() => {
                setSelectedZone(zone.id);
                setIsContributeModalOpen(true);
              }} className="w-full py-3 border border-white/10 rounded-sm text-sm text-white hover:bg-white/5 hover:border-[#D4AF37]/50 transition-colors" data-id="element-6426">
                    Restore This Zone
                  </button>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 5 & 6: School Restoration Mode & Misal */}
      <section id="schools" className="py-24 bg-[#0B0C10] relative border-t border-white/5 overflow-hidden" data-id="element-6427">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-[100px] pointer-events-none" data-id="element-6428" />

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-6429">
          <div className="text-center mb-16" data-id="element-6430">
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4" data-id="element-6431">
              For Educators
            </p>
            <h2 className="font-serif text-3xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-6432">
              Schools Can Restore Punjab Together
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto text-lg" data-id="element-6433">
              Schools can adopt the Lahore Treasury, create classroom Misls,
              collect cards, restore zones, and unlock eligibility for a
              sponsored trip to the live immersive experience.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8" data-id="element-6434">
            {/* School Dashboard Preview */}
            <div className="lg:col-span-7 bg-[#13151C] border border-white/10 rounded-2xl p-8 shadow-2xl" data-id="element-6435">
              <div className="flex items-start justify-between mb-8 pb-8 border-b border-white/5" data-id="element-6436">
                <div data-id="element-6437">
                  <div className="flex items-center gap-3 mb-2" data-id="element-6438">
                    <GraduationCap className="w-6 h-6 text-[#D4AF37]" data-id="element-6439" />
                    <h3 className="font-serif text-2xl text-white" data-id="element-6440">
                      Bramalea Secondary School
                    </h3>
                  </div>
                  <p className="text-white/40 text-sm" data-id="element-6441">Brampton, Ontario</p>
                </div>
                <div className="text-right" data-id="element-6442">
                  <p className="text-3xl font-serif text-[#D4AF37]" data-id="element-6443">42%</p>
                  <p className="text-xs text-white/40 uppercase tracking-wider" data-id="element-6444">
                    School Progress
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-id="element-6445">
                <div className="bg-black/30 p-4 rounded-lg border border-white/5" data-id="element-6446">
                  <p className="text-white/40 text-xs mb-1" data-id="element-6447">Classes Active</p>
                  <p className="text-xl text-white" data-id="element-6448">3</p>
                </div>
                <div className="bg-black/30 p-4 rounded-lg border border-white/5" data-id="element-6449">
                  <p className="text-white/40 text-xs mb-1" data-id="element-6450">Misls Created</p>
                  <p className="text-xl text-white" data-id="element-6451">5</p>
                </div>
                <div className="bg-black/30 p-4 rounded-lg border border-white/5" data-id="element-6452">
                  <p className="text-white/40 text-xs mb-1" data-id="element-6453">Cards Given</p>
                  <p className="text-xl text-white" data-id="element-6454">128</p>
                </div>
                <div className="bg-black/30 p-4 rounded-lg border border-white/5" data-id="element-6455">
                  <p className="text-white/40 text-xs mb-1" data-id="element-6456">Top Zone</p>
                  <p className="text-sm text-[#D4AF37] truncate" data-id="element-6457">
                    Lahore Court
                  </p>
                </div>
              </div>

              <div className="space-y-4" data-id="element-6458">
                <p className="text-sm font-medium text-white/80" data-id="element-6459">
                  Trip Eligibility Milestones
                </p>
                {[{
                label: 'School Treasury Badge',
                threshold: 25,
                achieved: true
              }, {
                label: 'Star of Punjab Challenge',
                threshold: 50,
                achieved: false
              }, {
                label: 'Treasury Night Invite',
                threshold: 75,
                achieved: false
              }, {
                label: 'Bus Sponsorship Priority',
                threshold: 100,
                achieved: false
              }].map((m, i) => <div key={i} className={`flex items-center gap-4 p-3 rounded-lg border ${m.achieved ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30' : 'bg-black/20 border-white/5'}`} data-id="element-6460">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${m.achieved ? 'bg-[#D4AF37] text-black' : 'bg-white/10 text-white/30'}`} data-id="element-6461">
                      {m.achieved ? <Star className="w-3 h-3" data-id="element-6462" /> : <span className="text-[10px]" data-id="element-6463">{m.threshold}%</span>}
                    </div>
                    <span className={`text-sm ${m.achieved ? 'text-[#D4AF37]' : 'text-white/50'}`} data-id="element-6464">
                      {m.label}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Misal Cards */}
            <div className="lg:col-span-5 space-y-4" data-id="element-6465">
              <h3 className="font-serif text-xl text-white mb-6" data-id="element-6466">
                Restore as a Misal
              </h3>
              {[{
              name: 'Sukerchakia Misl',
              rank: 'Court Class',
              score: 850,
              zone: 'Lahore Court'
            }, {
              name: 'Ahluwalia Misl',
              rank: 'Treasury Class',
              score: 620,
              zone: 'Frontier Defenders'
            }, {
              name: 'Ramgarhia Misl',
              rank: 'Darbar School',
              score: 410,
              zone: 'Sacred Punjab'
            }].map((misl, i) => <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 flex items-center justify-between hover:bg-white/10 transition-colors cursor-pointer" data-id="element-6467">
                  <div className="flex items-center gap-4" data-id="element-6468">
                    <div className="w-12 h-12 rounded-full bg-black/50 border border-[#D4AF37]/30 flex items-center justify-center" data-id="element-6469">
                      <Shield className="w-5 h-5 text-[#D4AF37]" data-id="element-6470" />
                    </div>
                    <div data-id="element-6471">
                      <p className="text-white font-medium" data-id="element-6472">{misl.name}</p>
                      <p className="text-xs text-[#D4AF37]" data-id="element-6473">{misl.rank}</p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-6474">
                    <p className="text-white font-serif" data-id="element-6475">{misl.score} pts</p>
                    <p className="text-[10px] text-white/40 uppercase" data-id="element-6476">
                      {misl.zone}
                    </p>
                  </div>
                </div>)}

              <div className="pt-6 mt-6 border-t border-white/10 flex flex-col gap-3" data-id="element-6477">
                <button className="w-full bg-[#D4AF37] text-black py-3 rounded-sm text-sm font-medium hover:bg-[#FDFBF7] transition-colors" data-id="element-6478">
                  Register Your School
                </button>
                <button className="w-full bg-transparent border border-white/20 text-white py-3 rounded-sm text-sm hover:bg-white/5 transition-colors" data-id="element-6479">
                  View School Leaderboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: Restoration Rewards */}
      <section className="py-24 bg-[#111218] relative border-t border-white/5" data-id="element-6480">
        <div className="max-w-7xl mx-auto px-6" data-id="element-6481">
          <div className="text-center mb-16" data-id="element-6482">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-6483">Restoration Unlocks</h2>
            <p className="text-white/60 max-w-2xl mx-auto" data-id="element-6484">
              As your school or misal restores the Punjab Memory Map, you unlock exclusive rewards, challenges, and live event access.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6485">
            {[{
            title: 'School Treasury Badge',
            threshold: '25% Restoration',
            unlocked: true,
            icon: Shield
          }, {
            title: 'Star of Punjab Challenge',
            threshold: '50% Restoration',
            unlocked: false,
            icon: Star
          }, {
            title: 'Treasury Night Invite Eligibility',
            threshold: '75% Restoration',
            unlocked: false,
            icon: Ticket
          }, {
            title: 'Bus Sponsorship Priority',
            threshold: '100% Restoration',
            unlocked: false,
            icon: Bus
          }, {
            title: 'Live Night Edition Collectible',
            threshold: 'Live Attendance',
            unlocked: false,
            icon: Gem
          }, {
            title: 'Witness of the Restored Court',
            threshold: 'Special Badge',
            unlocked: false,
            icon: Crown
          }].map((reward, i) => <div key={i} className={`p-6 rounded-xl border ${reward.unlocked ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30' : 'bg-black/40 border-white/10'} relative overflow-hidden`} data-id="element-6486">
                <reward.icon className={`w-8 h-8 mb-4 ${reward.unlocked ? 'text-[#D4AF37]' : 'text-white/20'}`} data-id="element-6487" />
                <h3 className={`font-serif text-xl mb-1 ${reward.unlocked ? 'text-white' : 'text-white/50'}`} data-id="element-6488">{reward.title}</h3>
                <p className={`text-sm ${reward.unlocked ? 'text-[#D4AF37]' : 'text-white/30'}`} data-id="element-6489">{reward.threshold}</p>
                {!reward.unlocked && <div className="absolute top-4 right-4 text-[10px] uppercase tracking-widest text-white/20 border border-white/10 px-2 py-1 rounded" data-id="element-6490">Locked</div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 8: Live Experience Connection */}
      <section className="py-24 bg-[#8B0000]/10 relative border-t border-[#8B0000]/20" data-id="element-6491">
        <div className="max-w-5xl mx-auto px-6 text-center" data-id="element-6492">
          <Ticket className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" data-id="element-6493" />
          <h2 className="font-serif text-3xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-6494">
            From Digital Restoration to the Live Court
          </h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto mb-12 leading-relaxed" data-id="element-6495">
            When a school restores enough of the Punjab Memory Map, it unlocks
            eligibility for a subsidized or sponsor-funded visit to The Kingdoms
            of Punjab live immersive experience.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-12" data-id="element-6496">
            {[{
            step: '1',
            text: 'Restore zones online'
          }, {
            step: '2',
            text: 'Reach trip eligibility'
          }, {
            step: '3',
            text: 'Sponsor funds bus'
          }, {
            step: '4',
            text: 'Attend Live Night'
          }].map((s, i) => <div key={i} className="bg-black/40 border border-[#D4AF37]/20 rounded-xl p-6 relative" data-id="element-6497">
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#D4AF37] text-black flex items-center justify-center font-bold text-sm" data-id="element-6498">
                  {s.step}
                </div>
                <p className="text-white/90 text-sm mt-2" data-id="element-6499">{s.text}</p>
              </div>)}
          </div>

          <button className="bg-[#D4AF37] text-black px-8 py-4 rounded-sm text-sm font-medium hover:bg-[#FDFBF7] transition-colors inline-flex items-center gap-2" data-id="element-6500">
            <HeartHandshake className="w-4 h-4" data-id="element-6501" />
            Sponsor a School Bus
          </button>
        </div>
      </section>

      {/* SECTION 9: Leaderboards */}
      <section className="py-24 bg-[#0B0C10] relative border-t border-white/5" data-id="element-6502">
        <div className="max-w-6xl mx-auto px-6" data-id="element-6503">
          <div className="text-center mb-16" data-id="element-6504">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-6505">
              Restoration Leaderboards
            </h2>
            <div className="flex justify-center gap-4 mt-8" data-id="element-6506">
              {['Schools', 'Classes / Misls', 'Monthly Challenge', 'Zones Restored'].map((tab, i) => <button key={i} className={`px-4 py-2 text-sm rounded-full border ${i === 0 ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-[#D4AF37]' : 'bg-transparent border-white/10 text-white/50 hover:text-white'}`} data-id="element-6507">
                  {tab}
                </button>)}
            </div>
          </div>

          <div className="bg-[#111218] border border-white/10 rounded-2xl overflow-hidden" data-id="element-6508">
            <div className="grid grid-cols-12 gap-4 p-4 border-b border-white/10 text-xs text-white/40 uppercase tracking-wider bg-black/20" data-id="element-6509">
              <div className="col-span-1 text-center" data-id="element-6510">Rank</div>
              <div className="col-span-5" data-id="element-6511">School / Misal</div>
              <div className="col-span-2 text-center" data-id="element-6512">Restoration %</div>
              <div className="col-span-2 text-center" data-id="element-6513">Cards</div>
              <div className="col-span-2 text-right pr-4" data-id="element-6514">Trip Status</div>
            </div>
            {[{
            rank: 1,
            name: 'Khalsa Community School',
            city: 'Brampton',
            progress: 92,
            cards: 450,
            status: 'Eligible'
          }, {
            rank: 2,
            name: 'Bramalea Secondary',
            city: 'Brampton',
            progress: 42,
            cards: 128,
            status: 'In Progress'
          }, {
            rank: 3,
            name: 'David Suzuki SS',
            city: 'Brampton',
            progress: 28,
            cards: 85,
            status: 'In Progress'
          }, {
            rank: 4,
            name: 'Lincoln M. Alexander',
            city: 'Mississauga',
            progress: 15,
            cards: 42,
            status: 'Started'
          }].map((row, i) => <div key={i} className="grid grid-cols-12 gap-4 p-4 border-b border-white/5 items-center hover:bg-white/5 transition-colors" data-id="element-6515">
                <div className="col-span-1 text-center" data-id="element-6516">
                  {i === 0 ? <Trophy className="w-5 h-5 text-[#D4AF37] mx-auto" data-id="element-6517" /> : <span className="text-white/50" data-id="element-6518">{row.rank}</span>}
                </div>
                <div className="col-span-5" data-id="element-6519">
                  <p className="text-white font-medium" data-id="element-6520">{row.name}</p>
                  <p className="text-white/40 text-xs" data-id="element-6521">{row.city}</p>
                </div>
                <div className="col-span-2 text-center" data-id="element-6522">
                  <span className="text-[#D4AF37] font-serif text-lg" data-id="element-6523">
                    {row.progress}%
                  </span>
                </div>
                <div className="col-span-2 text-center text-white/70" data-id="element-6524">
                  {row.cards}
                </div>
                <div className="col-span-2 text-right pr-4" data-id="element-6525">
                  <span className={`text-xs px-2 py-1 rounded-full ${row.status === 'Eligible' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-white/5 text-white/50 border border-white/10'}`} data-id="element-6526">
                    {row.status}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 4: Contribute Modal */}
      <AnimatePresence data-id="element-6527">
        {isContributeModalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-6" data-id="element-6528">
            <motion.div initial={{
          scale: 0.95,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          y: 20
        }} className="bg-[#111218] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl" data-id="element-6529">
              <div className="p-6 border-b border-white/10 flex items-center justify-between bg-black/20" data-id="element-6530">
                <div data-id="element-6531">
                  <h3 className="font-serif text-2xl text-white" data-id="element-6532">
                    Choose a Card to Restore Punjab
                  </h3>
                  <p className="text-white/50 text-sm mt-1" data-id="element-6533">
                    {selectedZone ? `Contributing to ${ZONES.find(z => z.id === selectedZone)?.name}` : 'Select a card from your collection to contribute.'}
                  </p>
                </div>
                <button onClick={() => setIsContributeModalOpen(false)} className="text-white/50 hover:text-white" data-id="element-6534">
                  <X className="w-6 h-6" data-id="element-6535" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1 bg-[#0B0C10]" data-id="element-6536">
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4 mb-6 flex items-start gap-3" data-id="element-6537">
                  <Sparkles className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" data-id="element-6538" />
                  <p className="text-sm text-[#D4AF37]/90" data-id="element-6539">
                    <strong data-id="element-6540">Important:</strong> Contributing a card does not
                    remove it from your collection. It adds its story power to
                    the Punjab Memory Map.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-6541">
                  {USER_CARDS.map(card => <div key={card.id} className="bg-[#13151C] border border-white/10 rounded-xl overflow-hidden group relative" data-id="element-6542">
                      {animatingCard === card.id && <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} className="absolute inset-0 bg-[#D4AF37]/20 z-20 flex items-center justify-center backdrop-blur-sm" data-id="element-6543">
                          <Sparkles className="w-8 h-8 text-[#D4AF37] animate-pulse" data-id="element-6544" />
                        </motion.div>}
                      <div className="aspect-[3/4] relative overflow-hidden bg-black" data-id="element-6545">
                        <img src={card.img} alt={card.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" data-id="element-6546" />
                        <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-md px-2 py-1 rounded text-[10px] text-[#D4AF37] border border-[#D4AF37]/30 uppercase tracking-wider" data-id="element-6547">
                          +{card.value} Pts
                        </div>
                      </div>
                      <div className="p-4" data-id="element-6548">
                        <p className="text-white font-serif truncate" data-id="element-6549">
                          {card.name}
                        </p>
                        <p className="text-white/40 text-xs mb-4" data-id="element-6550">
                          {card.rarity}
                        </p>
                        <button onClick={() => handleContribute(card.id)} disabled={animatingCard !== null} className="w-full bg-white/5 border border-white/10 text-white py-2 rounded text-xs hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37] transition-colors" data-id="element-6551">
                          Contribute to Restoration
                        </button>
                      </div>
                    </div>)}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}